/** What a document's own record page shows besides its facts: the file, the figures it became, and the
 * text EmitIQ read from it (U7).
 *
 * The page for a document used to be four facts and a sentence of engineering about a transition the
 * product does not have; an Auditor sampling evidence saw the evidence's name and nothing of it. It
 * is now the document and what came of it:
 *
 * - **Open the file** - the short-lived link, issued only once this page is on screen, as the Document
 *   tab of an activity record does.
 * - **Figures from this document** - the activity records that cite it, each a link to its own record,
 *   with the figure that was priced from it and a restated one marked as no longer counted. Read from
 *   the document's usage (`GET /evidence/{id}/usage`), the same read the review screen uses. Nothing
 *   here is resolved from what the document *says* (SEC-05): the link is the record's own citation of
 *   this document.
 * - **The text** - the shared `SourceTextPane`. For a document a person accepted with no text
 *   positions recorded it says so, instead of "nobody has read this document" under a state that
 *   says it was verified.
 */

import { Link } from "@tanstack/react-router";

import { useDocumentUrl, useUsage } from "../api/evidence";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { StatusChip } from "../components/ui";
import { shortDate, statedQuantity, tonnes, unitLabel } from "../lib/format";
import { DocumentHistory } from "./RecordDocumentHistory";
import { SourceTextPane } from "./SourceText";
import { UsageIssued } from "./UsageIssued";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import styles from "./Screen.module.css";
import own from "./Record.module.css";

export function RecordDocumentView({ documentId, state }: { documentId: string; state: string }) {
  const accepted = state === "verified";
  // The link is issued on opening the page for a document a person may open at all; a refused or
  // quarantined one answers a refusal that is shown as such and not as a missing link.
  const url = useDocumentUrl(documentId, true);
  const usage = useUsage(documentId, accepted);

  return (
    <>
      <Panel title="The file" column="1 / 9">
        <p className={styles.note}>
          {url.error ? (
            <StatusChip tone="crit" size="sm">
              No link could be issued
            </StatusChip>
          ) : url.data ? (
            <a
              className={styles.fixLink}
              href={url.data}
              target="_blank"
              rel="noreferrer"
              data-testid="document-open-file"
            >
              Open the file
            </a>
          ) : (
            <span data-testid="document-open-file-pending">Issuing a link…</span>
          )}
        </p>
      </Panel>

      <Panel
        title="Figures from this document"
        sub={usage.data ? `${String(usage.data.activities.length)} record${usage.data.activities.length === 1 ? "" : "s"}` : undefined}
        column="1 / 9"
      >
        {!accepted ? (
          <Empty>No figure yet: a person has not accepted this document.</Empty>
        ) : usage.error ? (
          <ReadFailed
            of="The figures from this document"
            error={usage.error}
            onRetry={() => void usage.refetch()}
          />
        ) : !usage.data ? (
          <Empty>Loading…</Empty>
        ) : usage.data.activities.length === 0 ? (
          <Empty>No figure came from this document.</Empty>
        ) : (
          <ul className={own.docFigures} data-testid="document-figures">
            {usage.data.activities.map((activity) => (
              <li key={activity.activity_record_id} className={own.docFigure}>
                <Link
                  className={styles.fixLink}
                  to="/records/$recordId"
                  params={{ recordId: activity.activity_record_id }}
                  search={keepSearch}
                >
                  {activity.facility_name}
                </Link>
                <span className={own.docFigureMeta}>
                  {statedQuantity(activity.quantity)} {unitLabel(activity.unit)} · {shortDate(activity.period_start)} –{" "}
                  {shortDate(activity.period_end)}
                </span>
                <span className={own.docFigureTonnes}>
                  {activity.calculations.length === 0
                    ? "no figure"
                    : activity.calculations.map((calculation) => (
                        // Every figure keeps its receipt (rule 3): the link opens it, with the basis and
                        // the window carried rather than reset.
                        <Link
                          key={calculation.calculation_id}
                          className={styles.fixLink}
                          to="/emissions"
                          search={(prev: Partial<ScreenSearch>) => ({
                            ...keepSearch(prev),
                            receipt: calculation.calculation_id,
                          })}
                        >
                          {tonnes(calculation.kgco2e, 2)} tCO₂e
                        </Link>
                      ))}
                </span>
                {activity.is_effective ? null : (
                  <StatusChip tone="warn" size="sm">
                    Superseded
                  </StatusChip>
                )}
              </li>
            ))}
          </ul>
        )}
        {usage.data ? <UsageIssued filings={usage.data.filings} /> : null}
      </Panel>

      <DocumentHistory documentId={documentId} state={state} />

      <SourceTextPane
        documentId={documentId}
        selected={null}
        onSelect={() => undefined}
        column="1 / 13"
        accepted={accepted}
      />
    </>
  );
}
